import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { analyticsKeys, moderationKeys, skillKeys } from "@/lib/query";
import type { ModerationRow, ModerationStats } from "@/lib/skills/moderation";

export interface ModerationData {
  rows: ModerationRow[];
  stats: ModerationStats;
}

export function useModeration() {
  return useQuery({
    queryKey: moderationKeys.all,
    queryFn: async (): Promise<ModerationData> => {
      const res = await fetch("/api/moderation");
      if (!res.ok) throw new Error("Failed to load moderation data");
      const json: { data: ModerationData } = await res.json();
      return json.data;
    },
  });
}

/** Toggle publication from the moderation table; invalidates the catalog + analytics too. */
export function useModerationPublish() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      slug: string;
      title: string;
      published: boolean;
    }): Promise<{ data: { title: string; published: boolean } }> => {
      const res = await fetch(`/api/skills/${input.slug}/publish`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ published: input.published }),
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(typeof j.error === "string" ? j.error : "Publish toggle failed");
      }
      const json: { data: { title: string; published: boolean } } = await res.json();
      return json;
    },
    onSuccess: (json) => {
      qc.invalidateQueries({ queryKey: moderationKeys.all });
      qc.invalidateQueries({ queryKey: skillKeys.all });
      qc.invalidateQueries({ queryKey: analyticsKeys.all });
      toast.success(
        json.data.published ? `Published ${json.data.title}` : `Unpublished ${json.data.title}`,
      );
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Publish toggle failed"),
  });
}
